/**
 * Derived "single active code" selectors for the grade panel roll-up
 * (CDMX-15251). Pure functions over the gradeBenchPanel slice so they are
 * unit-testable without a store.
 *
 * The active code is the one whose questions are expanded; every other code
 * rolls up. There is no stored "active id" for the auto flow — it is derived
 * from per-code completion + display order, which makes auto-advance fall out
 * for free and avoids initialisation races. `manualActiveCodeId` lets the
 * grader jump to / review any code.
 */

type GradeBenchPanelSlice = Pick<
  Accelerate.GradeBenchPanelState,
  'invoices' | 'codeCompletion' | 'manualActiveCodeId'
>;

const bySequenceNumber = (a: Accelerate.CQACode, b: Accelerate.CQACode) =>
  (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0);

/**
 * Code ids (as strings) in visual render order: for each invoice, each
 * transaction, the procedures (CPT) then modifiers (MOD) then diagnoses (ICD),
 * each group sorted by sequenceNumber — matching the Transaction render order.
 */
export const selectOrderedCodeIds = (
  gradeBenchPanel: GradeBenchPanelSlice | undefined
): string[] => {
  const ids: string[] = [];
  (gradeBenchPanel?.invoices ?? []).forEach(invoice => {
    (invoice.transactions ?? []).forEach(transaction => {
      [...(transaction.procedures ?? [])]
        .sort(bySequenceNumber)
        .forEach(code => ids.push(String(code.id)));
      [...(transaction.modifiers ?? [])]
        .sort(bySequenceNumber)
        .forEach(code => ids.push(String(code.id)));
      [...(transaction.diagnoses ?? [])]
        .sort(bySequenceNumber)
        .forEach(code => ids.push(String(code.id)));
    });
  });
  return ids;
};

/**
 * The code that should be expanded:
 *   1. `manualActiveCodeId` when it is still a present code (jump / review);
 *   2. otherwise the first code (in order) that is not complete;
 *   3. otherwise null — everything is complete and rolled up.
 *
 * Codes that have not yet reported completion are treated as incomplete, so on
 * first paint the first code is active and the rest collapse.
 */
export const selectActiveCodeId = (
  gradeBenchPanel: GradeBenchPanelSlice | undefined
): string | null => {
  const ordered = selectOrderedCodeIds(gradeBenchPanel);
  const manual = gradeBenchPanel?.manualActiveCodeId ?? null;
  if (manual && ordered.includes(manual)) {
    return manual;
  }
  const completion = gradeBenchPanel?.codeCompletion ?? {};
  for (const id of ordered) {
    if (!completion[id]) {
      return id;
    }
  }
  return null;
};
